/**
 * Kunden — T1 List-View (UX-Inventar §5.2).
 * T2 update: uses shared mock data with stable ids; row-click → /customers/$id.
 * V10-J: data via useKundeRows (live GET /customers, mock fallback) + loading/error states.
 * UI-U3 (AC 3): Risiko as a soft danger badge w/ warn icon; conditional Betreuer
 * column (hidden until the wire delivers it); "Mehr laden" on the keyset cursor.
 * [Source: docs/stories/t2-entity-detail.md#task-5]
 * [Source: docs/stories/ui-u3-core-restyle.md AC3/AC9]
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { TableCell } from '../../components/ui/Table';
import { EntityLink } from '../../components/ui/EntityLink';
import { StatusDot } from '../../components/ui/StatusDot';
import { Toast, type ToastTone } from '../../components/ui/Toast';
import { chf } from '../../lib/format';
import { ApiError, apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { qk } from '../../lib/query-keys';
import { KUNDE_STATUS_META, MANDATE_TRIGGER_STATUSES, TERMINATION_TRIGGER_STATUSES } from './customer-status-meta';
import { useKundeRows } from './hooks/useKundeRows';
import { useCustomerStatusMutation } from './hooks/useCustomerStatusMutation';
import { StatusSelect } from './components/StatusSelect';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { NewCustomerWizard } from './components/NewCustomerWizard';
import { MandateWizardDialog } from '../mandate/MandateWizardDialog';
import { MandatAufloesenDialog } from '../mandate/termination/MandatAufloesenDialog';
import type { KundeRow } from './api/customers-api';
import { inaktivSeitLabel } from '@brokly/shared';

const num = 'text-right mono-num';

/** Debounce window (ms) between the last keystroke and the `?q=` request (useDocumentRows precedent). */
const KUNDEN_SEARCH_DEBOUNCE_MS = 300;

/**
 * Saved-view presets, filtered CLIENT-side over the loaded rows (the
 * InvoicesScreen precedent). "Risiko-Kunden" is permanently empty on the live
 * wire: `risiko` is not delivered yet (issue 28). Keeping or dropping the pill
 * is a product call, not part of the search fix.
 *
 * "Unbestätigt" is the exception: a client-side cut would miss unconfirmed records past the
 * current page and mislabel its own count as "unconfirmed on this page", so that pill goes to
 * the SERVER (`unconfirmed_only`). Its `match` still reads the api's own `is_unconfirmed` on the
 * row: the mock row builder has no server to apply `unconfirmed_only`, so a no-op match would
 * put every known client under a pill that calls them unconfirmed.
 */
const KUNDE_PRESETS: { label: string; match: (k: KundeRow) => boolean }[] = [
  { label: 'Unbestätigt', match: (k) => k.isUnconfirmed === true },
  { label: 'Alle', match: () => true },
  { label: 'Aktiv', match: (k) => k.status === 'aktiv' },
  // E1 removed the invented 'prospect' status, so the pill counts a real one.
  // 'provisorisch' is the lifecycle stage a not-yet-signed client sits in.
  { label: KUNDE_STATUS_META.provisorisch.label, match: (k) => k.status === 'provisorisch' },
  { label: 'Risiko-Kunden', match: (k) => Boolean(k.risiko) },
];

/** The "Unbestätigt" pill's index in KUNDE_PRESETS: the one pill that filters SERVER-side. */
const UNCONFIRMED_VIEW_INDEX = 0;
/** The "Alle" pill's index in KUNDE_PRESETS, the screen's default view. */
const DEFAULT_VIEW_INDEX = 1;

/** How long the confirm / decline notice stays on screen (ms). */
const NOTICE_MS = 3000;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function CustomersScreen() {
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeView, setActiveView] = useState(DEFAULT_VIEW_INDEX);
  const [search, setSearch] = useState('');
  // The text search is the SERVER leg (GET /customers?q=, ILIKE over person /
  // firma name) so it reaches rows the keyset has not loaded yet; debounced so a
  // keystroke is not a request.
  const q = useDebounced(search.trim(), KUNDEN_SEARCH_DEBOUNCE_MS);
  // The Unbestätigt pill re-queries the server with `unconfirmed_only`, rather than cutting the
  // loaded page client-side (see the KUNDE_PRESETS header note).
  const showUnconfirmedOnly = activeView === UNCONFIRMED_VIEW_INDEX;
  const { data = [], isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useKundeRows({
      ...(q ? { q } : {}),
      ...(showUnconfirmedOnly ? { unconfirmed_only: true } : { customers_only: true }),
    });
  // The other pills count the customers page even while the Unbestätigt view is active. With the
  // pill off these are the rows above (same query key, no second request).
  const {
    data: customerRows = [],
    isLoading: customerRowsLoading,
    isError: customerRowsError,
  } = useKundeRows({ ...(q ? { q } : {}), customers_only: true });

  // The Unbestätigt pill's own count: a TRUTHFUL total across the whole tenant (`include_total`),
  // never "unconfirmed on the currently loaded page". Live only: mock mode has no backend to
  // total against, so the pill shows no number rather than inventing one. `q` rides along, or a
  // total that ignored the search box would read as the count of the unconfirmed records on
  // screen.
  const live = isLiveData();
  const unconfirmedTotalQuery = useQuery<number | null>({
    queryKey: qk.customers({ ...(q ? { q } : {}), unconfirmed_only: true, include_total: true }),
    queryFn: () =>
      apiClient.customers
        .list({ ...(q ? { q } : {}), unconfirmed_only: true, limit: 1, include_total: true })
        // react-query's queryFn may never resolve `undefined` (it means "no data yet"
        // internally); an older api build omitting total_count reads as null instead.
        .then((res) => res.page.total_count ?? null),
    enabled: live,
    staleTime: 60_000,
  });
  const unconfirmedCount = live ? (unconfirmedTotalQuery.data ?? undefined) : undefined;

  // E1: the grid writes the status directly. The api guard stays the hard line.
  // mutateAsync (not the mutation object) is the stable identity to memo on.
  const { mutateAsync: changeStatus } = useCustomerStatusMutation();
  // E6 + PR #66 decision 5: a mandate-generating pick opens the mandate wizard here too, as on the
  // customer screen, so the list can start a mandate wherever its menu offers one (a leer customer
  // included). The row keeps its server status until the wizard sends.
  const [mandateWizard, setMandateWizard] = useState<{
    subjectType: KundeRow['subjectType'];
    subjectId: string;
    name: string;
    targetStatus: KundeRow['status'];
  } | null>(null);
  // P6: the row whose «Gekündigt» pick opened the dialog «Mandat auflösen».
  const [termination, setTermination] = useState<{ subjectType: KundeRow['subjectType']; subjectId: string } | null>(null);
  const { can } = useEffectivePermissions();
  // E1: transition is the everyday right, override the admin escape hatch.
  const canTransition = can('customer_status.transition');
  const canOverride = can('customer_status.override');
  const canWriteCrm = can('crm.write');

  // The Unbestätigt view's row buttons. The api owns the outcome (409 when the record is no longer
  // open); this only reports it. One timer owns the notice so an early one never clears a later one.
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ text: string; tone: ToastTone } | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const showNotice = useCallback((text: string, tone: ToastTone = 'ok') => {
    setNotice({ text, tone });
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);
  useEffect(
    () => () => {
      if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    },
    [],
  );
  const refreshAfterDecision = () => {
    void qc.invalidateQueries({ queryKey: ['customers'] });
    void qc.invalidateQueries({ queryKey: ['posteingang'] });
  };
  const { mutate: confirmRecord, isPending: confirming } = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.confirmCustomer(subjectId),
    onSuccess: () => {
      showNotice('Als Kunde übernommen.');
      refreshAfterDecision();
    },
    onError: (err) =>
      showNotice(
        err instanceof ApiError ? err.message : 'Kunde konnte nicht übernommen werden.',
        'alert',
      ),
  });
  const { mutate: declineRecord, isPending: declining } = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.declineCustomer(subjectId),
    onSuccess: () => {
      showNotice('Als «Kein Kunde» markiert.');
      refreshAfterDecision();
    },
    onError: (err) =>
      showNotice(
        err instanceof ApiError ? err.message : 'Konnte nicht als «Kein Kunde» markiert werden.',
        'alert',
      ),
  });

  // The status preset cuts the LOADED rows; `q` and `customers_only` (for Unbestätigt,
  // `unconfirmed_only`) already cut them server-side.
  const visible = useMemo(() => {
    const preset = KUNDE_PRESETS[activeView] ?? KUNDE_PRESETS[DEFAULT_VIEW_INDEX]!;
    return data.filter(preset.match);
  }, [data, activeView]);

  const totalPraemie = visible.reduce((acc, k) => acc + k.jahrespraemie, 0);
  // Column set follows the whole loaded page, not the preset cut, so switching
  // pills never adds or removes a column.
  const hasBetreuer = data.some((k) => k.betreuer.trim() !== '');

  const columns = useMemo<ColumnDef<KundeRow>[]>(() => {
    const cols: ColumnDef<KundeRow>[] = [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <EntityLink
            onActivate={() => void navigate({ to: '/customers/$id', params: { id: row.original.id } })}
            className="font-medium"
          >
            {row.original.name}
          </EntityLink>
        ),
      },
      {
        // NOTE: the wire field is still named `branche` but carries the firm's Ort/city
        // (we don't collect an industry/Branche). Labelled "Ort" until the field is renamed.
        accessorKey: 'branche',
        header: 'Ort',
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.branche}</span>,
      },
    ];

    if (hasBetreuer) {
      cols.push({
        accessorKey: 'betreuer',
        header: 'Betreuer',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.betreuer || '-'}</span>
        ),
      });
    }

    cols.push(
      {
        accessorKey: 'policen',
        header: 'Policen',
        cell: ({ row }) => <div className={num}>{row.original.policen}</div>,
      },
      {
        accessorKey: 'jahrespraemie',
        header: 'Jahresprämie',
        cell: ({ row }) => <div className={num}>{chf(row.original.jahrespraemie)}</div>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        // E1: "directly mutable in the customer-overview table grid". In the Unbestätigt view the
        // label is plain and the two buttons are the only way to sort a record.
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {showUnconfirmedOnly ? (
              <StatusDot
                tone={KUNDE_STATUS_META[row.original.status].tone}
                label={KUNDE_STATUS_META[row.original.status].label}
              />
            ) : (
              <StatusSelect
                status={row.original.status}
                allowedTransitions={row.original.allowedTransitions}
                canTransition={canTransition}
                canOverride={canOverride}
                ariaLabel={`Kundenstatus ${row.original.name}`}
                onChange={(next) => {
                  // P6: «Gekündigt» opens the dialog «Mandat auflösen» instead of writing.
                  if (TERMINATION_TRIGGER_STATUSES.includes(next)) {
                    setTermination({ subjectType: row.original.subjectType, subjectId: row.original.id });
                    return Promise.resolve();
                  }
                  // E6: a mandate-generating status opens the wizard instead of writing.
                  if (MANDATE_TRIGGER_STATUSES.includes(next)) {
                    setMandateWizard({
                      subjectType: row.original.subjectType,
                      subjectId: row.original.id,
                      name: row.original.name,
                      targetStatus: next,
                    });
                    return Promise.resolve();
                  }
                  return changeStatus({
                    subjectType: row.original.subjectType,
                    subjectId: row.original.id,
                    toStatus: next,
                    // Anything outside the legal list is only ever offered to an
                    // override holder, so it must go to the override route.
                    mode: row.original.allowedTransitions.includes(next)
                      ? 'transition'
                      : 'override',
                  });
                }}
              />
            )}
            {/* P6 (7.14): an inaktiv customer reads «inaktiv seit <Zurich day of the run>». */}
            {row.original.status === 'inaktiv' && (
              <span className="text-xs text-muted-foreground" data-testid={`inaktiv-seit-${row.original.id}`}>
                {inaktivSeitLabel(row.original.inactivatedAt ?? null)}
              </span>
            )}
            {row.original.isUnconfirmed && <Badge variant="neutral">Unbestätigt</Badge>}
            {row.original.risiko && (
              <Badge variant="danger">
                <AlertTriangle aria-hidden /> Risiko
              </Badge>
            )}
            {showUnconfirmedOnly && canWriteCrm && row.original.isUnconfirmed && (
              <>
                <Button
                  size="sm"
                  disabled={confirming || declining}
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmRecord(row.original.id);
                  }}
                >
                  Als Kunde übernehmen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={confirming || declining}
                  onClick={(e) => {
                    e.stopPropagation();
                    declineRecord(row.original.id);
                  }}
                >
                  Kein Kunde
                </Button>
              </>
            )}
          </div>
        ),
      },
    );

    return cols;
  }, [
    hasBetreuer,
    navigate,
    canTransition,
    canOverride,
    changeStatus,
    showUnconfirmedOnly,
    canWriteCrm,
    confirmRecord,
    declineRecord,
    confirming,
    declining,
  ]);

  const summary = (
    <>
      {columns.map((col, i) => {
        const key = (col as { accessorKey?: string }).accessorKey ?? String(i);
        if (i === 0) return <TableCell key={key} className="font-medium">Summe</TableCell>;
        if (key === 'jahrespraemie') {
          return (
            <TableCell key={key} className={`font-medium ${num}`}>
              {chf(totalPraemie)}
            </TableCell>
          );
        }
        return <TableCell key={key} />;
      })}
    </>
  );

  // Pill counts stay on the whole customers page, also while the Unbestätigt view is active; the
  // toolbar count below is "visible (after the preset) von loaded", the InvoicesScreen convention.
  // With the server-side `q` the loaded page is ALREADY filtered, so a
  // narrowing search can legitimately read "3 von 3".
  //
  // Unbestätigt does NOT count the loaded page ("unconfirmed on this page" is untruthful past
  // page one): its count is the server's include_total, undefined while loading or in mock mode.
  // The others show no number while the customers page loads or failed, never a false zero.
  const customersCountable = !customerRowsLoading && !customerRowsError;
  const views = KUNDE_PRESETS.map((p, i) => ({
    label: p.label,
    count:
      i === UNCONFIRMED_VIEW_INDEX
        ? unconfirmedCount
        : customersCountable
          ? customerRows.filter(p.match).length
          : undefined,
  }));

  return (
    <>
    <ListView
      title="Kunden"
      views={views}
      activeView={activeView}
      onViewChange={setActiveView}
      searchValue={search}
      onSearchChange={setSearch}
      count={`${visible.length} von ${data.length}`}
      searchPlaceholder="Kunde, Ort …"
      rightAction={
        <Button size="sm" onClick={() => setWizardOpen(true)}>
          <Plus className="size-3.5" /> Neuer Kunde
        </Button>
      }
    >
      {isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Lade Kunden …</p>
      ) : isError ? (
        <p className="py-8 text-center text-sm text-muted-foreground" role="alert">
          Kunden konnten nicht geladen werden.
        </p>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={visible}
            onRowClick={(row) => void navigate({ to: '/customers/$id', params: { id: row.id } })}
            summary={summary}
          />
          {hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                {isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
              </Button>
            </div>
          )}
        </>
      )}
    </ListView>
    {notice && <Toast tone={notice.tone}>{notice.text}</Toast>}
    <NewCustomerWizard
      open={wizardOpen}
      onClose={() => setWizardOpen(false)}
      onCreated={(_subjectType, subjectId) => {
        setWizardOpen(false);
        void navigate({ to: '/customers/$id', params: { id: subjectId } });
      }}
    />
    {termination !== null && (
      <MandatAufloesenDialog
        subjectType={termination.subjectType}
        subjectId={termination.subjectId}
        onClose={() => setTermination(null)}
        onDone={() => setTermination(null)}
      />
    )}
    {mandateWizard !== null && (
      <MandateWizardDialog
        subjectType={mandateWizard.subjectType}
        subjectId={mandateWizard.subjectId}
        targetStatus={mandateWizard.targetStatus}
        subjectName={mandateWizard.name}
        // Cancel: nothing was written, so nothing is undone.
        onCancel={() => setMandateWizard(null)}
        // Sent: the api wrote the status with the overlay; refetch so the row shows it.
        onSent={() => {
          setMandateWizard(null);
          void qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        // A released or sent mandate of this kind blocks the prepare: open the customer, whose
        // Übersicht shows it with Zustellen / Freigabe zurückziehen.
        onOpenMandate={() => {
          const subjectId = mandateWizard.subjectId;
          setMandateWizard(null);
          void navigate({ to: '/customers/$id', params: { id: subjectId } });
        }}
        // A Register-Befund was recorded or its Vorgang aborted: the flag and the tasks follow.
        onOpenCustomer={() => {
          const subjectId = mandateWizard.subjectId;
          setMandateWizard(null);
          void navigate({ to: '/customers/$id', params: { id: subjectId } });
        }}
        onLockChanged={() => {
          void qc.invalidateQueries({ queryKey: qk.registerBefund(mandateWizard.subjectType, mandateWizard.subjectId) });
          void qc.invalidateQueries({ queryKey: ['tasks'] });
        }}
      />
    )}
    </>
  );
}
